import React, { useEffect, useState } from "react";
import { Box, Chip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { format } from "date-fns";
import MaterialTable from "../../../Components/MaterialTable";
import { IColumn } from "../../../Components/MaterialTable/MaterialTable";
import { formatCurrency } from "../../../utils/formatCurrency";
import { formatDate } from "../../../utils/formatDate";
import {
	Settlement,
	SETTLEMENT_STATUS_COLOR,
	SETTLEMENT_STATUS_LABEL,
	SettlementStatusEnum
} from "../../../store/services/affiliate/dto/affiliate.dto";
import {
	useLazyGetSettlementsQuery,
	useUpdateSettlementStatusMutation
} from "../../../store/services/admin/admin.service";
import { useAppSelector } from "../../../store";
import { getFullAffiliateState } from "../../../store/slices/affiliate/affiliate.slice";
import SettlementModal from "./SettlementModal";
import AffiliateSettlementsCardsList from "./AffiliateSettlementsCardsList";
import { notifyError } from "../../../utils/NotifyError";
import { IError } from "../../../utils/getErrorMessage";

const PER_PAGE = 20;

const AffiliateSettlements = () => {
	const theme = useTheme();
	const isSmallerScreen = useMediaQuery(theme.breakpoints.down("md"));
	const { settlements, settlementsTotalItems, isFetchingSettlements } = useAppSelector(getFullAffiliateState);
	const [FetchSettlements] = useLazyGetSettlementsQuery();
	const [callUpdateSettlementStatus] = useUpdateSettlementStatusMutation();
	const [selectedMonth, setSelectedMonth] = useState<Date>(new Date());
	const [selectedRow, setSelectedRow] = useState<Settlement | null>(null);
	const [page, setPage] = useState(1);

	useEffect(() => {
		setPage(1);
		FetchSettlements({ month: format(selectedMonth, "yyyy-MM"), page: 1, perPage: PER_PAGE });
	}, [selectedMonth]);

	const handlePageChange = (newPage: number) => {
		if (newPage > 0) {
			setPage(newPage);
			FetchSettlements({ month: format(selectedMonth, "yyyy-MM"), page: newPage, perPage: PER_PAGE });
		}
	};

	const handleStatusChange = (id: string, status: SettlementStatusEnum) => {
		callUpdateSettlementStatus({ id, status }).then(({ error }) => {
			if (error) {
				notifyError(error as IError);
			}
		});
	};

	const columns: IColumn<Settlement>[] = [
		{
			label: "First Name",
			dataKey: "firstName",
			Cell: ({ rowData }) => <Typography fontSize={13}>{rowData.firstName}</Typography>
		},
		{
			label: "Last Name",
			dataKey: "lastName",
			Cell: ({ rowData }) => <Typography fontSize={13}>{rowData.lastName}</Typography>
		},
		{
			label: "Business Name",
			dataKey: "businessName",
			Cell: ({ rowData }) => <Typography fontSize={13}>{rowData.businessName}</Typography>
		},
		{
			label: "Total Sales",
			dataKey: "totalSales",
			Cell: ({ rowData }) => <Typography fontSize={13}>{formatCurrency(rowData.totalSales, "EGP")}</Typography>
		},
		{
			label: "Net Sales",
			dataKey: "netSales",
			Cell: ({ rowData }) => (
				<Typography fontSize={13} fontWeight={600}>
					{formatCurrency(rowData.netSales, "EGP")}
				</Typography>
			)
		},
		{
			label: "Status",
			dataKey: "status",
			Cell: ({ rowData }) => {
				return (
					<Chip
						label={SETTLEMENT_STATUS_LABEL[rowData.status]}
						color={SETTLEMENT_STATUS_COLOR[rowData.status]}
						size={"small"}
					/>
				);
			}
		},
		{
			label: "Paid At",
			dataKey: "paidAt",
			Cell: ({ rowData }) => (
				<Typography fontSize={13}>{rowData.paidAt ? formatDate(rowData.paidAt, "Africa/Cairo", "PP") : "-"}</Typography>
			)
		}
	];

	const Filters = (
		<DatePicker
			label={"Month"}
			views={["year", "month"]}
			value={selectedMonth}
			onChange={newValue => newValue && setSelectedMonth(newValue)}
			slotProps={{ textField: { size: "small" } }}
		/>
	);

	return (
		<Box>
			{isSmallerScreen ? (
				<Box>
					<Box display={"flex"} justifyContent={"flex-end"} mb={2}>
						{Filters}
					</Box>
					<AffiliateSettlementsCardsList
						data={settlements}
						onCardClick={row => setSelectedRow(row)}
						page={page}
						perPage={PER_PAGE}
						totalItems={settlementsTotalItems}
						onPageChange={handlePageChange}
					/>
				</Box>
			) : (
				<MaterialTable
					tableLabel={"Affiliate Settlements"}
					columns={columns}
					data={settlements}
					isLoading={isFetchingSettlements}
					onRowClick={row => setSelectedRow(row)}
					filters={<Box>{Filters}</Box>}
					page={page}
					rowsPerPage={PER_PAGE}
					totalItems={settlementsTotalItems}
					onPageChange={handlePageChange}
				/>
			)}
			{selectedRow && (
				<SettlementModal
					open={!!selectedRow}
					onClose={() => setSelectedRow(null)}
					settlement={selectedRow}
					status={selectedRow.status}
					onStatusChange={status => handleStatusChange(selectedRow.id, status)}
				/>
			)}
		</Box>
	);
};

export default AffiliateSettlements;
